'use client';

import { c as _c } from "react/compiler-runtime";
import { useState } from 'react';
import { useQueryState } from 'nuqs';
import { cx } from 'class-variance-authority';
import { Button, Checkbox, Icon, Modal } from '~/components/primitives';
import { useI18n } from '~/features/i18n';
import { resolveModeSwitch } from '../_lib/rules';
import { useCatalog_EditMode_Session, VIEW_TAB_QP, type Catalog_EditMode_Rules } from './session';
type CatalogMode = Catalog_EditMode_Rules['activityFilters']['groupIDs']['mode'];

/** Two radio cards (Exclusive | Inclusive) at the top of the Rules pane.
 * Auto-add checkbox is nested *inside* the Exclusive card per the design
 * handoff, and is only visible while Exclusive is the active mode.
 *
 * Switching modes opens a confirmation dialog: confirming clears
 * `groupIDs.IDs` (so the allowlist for INCLUSIVE / disallowlist for EXCLUSIVE
 * starts empty) and resets the view tab to `visible`. Other rules survive. */
export const Catalog_EditMode_ModeSwitch: React.FC = () => {
  const $ = _c(48);
  const i18n = useI18n();
  const session = useCatalog_EditMode_Session();
  const [, setView] = useQueryState("view", VIEW_TAB_QP);
  const [pendingMode, setPendingMode] = useState(null);
  let t0;
  if ($[0] !== session.rules.activityFilters.groupIDs.mode) {
    t0 = mode => {
      if (mode === session.rules.activityFilters.groupIDs.mode) {
        return;
      }
      setPendingMode(mode);
    };
    $[0] = session.rules.activityFilters.groupIDs.mode;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const onCardClick = t0;
  let t1;
  if ($[2] !== pendingMode || $[3] !== session || $[4] !== setView) {
    t1 = () => {
      if (!pendingMode) {
        return;
      }
      session.setRules(prev => resolveModeSwitch(pendingMode, prev));
      setView("visible");
      setPendingMode(null);
    };
    $[2] = pendingMode;
    $[3] = session;
    $[4] = setView;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const confirmModeSwitch = t1;
  const allowlistCount = session.rules.activityFilters.groupIDs.IDs?.length ?? 0;
  let t2;
  if ($[6] !== i18n) {
    t2 = i18n._("bizz.booking.edit.mode.title", undefined, {
      message: "Catalog mode"
    });
    $[6] = i18n;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== t2) {
    t3 = <h3 className="text-sm font-semibold text-blue">{t2}</h3>;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const t4 = session.rules.activityFilters.groupIDs.mode === "EXCLUSIVE";
  let t5;
  if ($[10] !== onCardClick) {
    t5 = () => onCardClick("EXCLUSIVE");
    $[10] = onCardClick;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== i18n) {
    t6 = i18n._("bizz.booking.edit.mode.exclusive.title", undefined, {
      message: "Exclusive"
    });
    $[12] = i18n;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== i18n) {
    t7 = i18n._("bizz.booking.edit.mode.exclusive.description", undefined, {
      message: "Show all activities by default. Pick the ones to exclude from the catalog."
    });
    $[14] = i18n;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== i18n || $[17] !== session) {
    t8 = session.rules.activityFilters.groupIDs.mode === "EXCLUSIVE" ? <label htmlFor="bizz-booking-auto-add" className="flex cursor-pointer items-start gap-3 text-primary"><Checkbox id="bizz-booking-auto-add" className="mt-0.5" checked={session.rules.automaticallyAddNewActivitiesToSelection} onChange={e => {
        const checked = e.target.checked;
        session.setRules(prev_0 => ({
          ...prev_0,
          automaticallyAddNewActivitiesToSelection: checked
        }));
      }} /><span className="text-xs leading-tight">{i18n._("bizz.booking.edit.mode.exclusive.autoAdd", undefined, {
          message: "Automatically include new activities in the catalog"
        })}</span></label> : null;
    $[16] = i18n;
    $[17] = session;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== t4 || $[20] !== t5 || $[21] !== t6 || $[22] !== t7 || $[23] !== t8) {
    t9 = <ModeCard isActive={t4} onClick={t5} testId="bizz-booking-mode-exclusive" title={t6} description={t7}>{t8}</ModeCard>;
    $[19] = t4;
    $[20] = t5;
    $[21] = t6;
    $[22] = t7;
    $[23] = t8;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  const t10 = session.rules.activityFilters.groupIDs.mode === "INCLUSIVE";
  let t11;
  if ($[25] !== onCardClick) {
    t11 = () => onCardClick("INCLUSIVE");
    $[25] = onCardClick;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] !== i18n) {
    t12 = i18n._("bizz.booking.edit.mode.inclusive.title", undefined, {
      message: "Inclusive"
    });
    $[27] = i18n;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  let t13;
  if ($[29] !== i18n) {
    t13 = i18n._("bizz.booking.edit.mode.inclusive.description", undefined, {
      message: "Hide all activities by default. Pick the ones to include in the catalog."
    });
    $[29] = i18n;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  let t14;
  if ($[31] !== t10 || $[32] !== t11 || $[33] !== t12 || $[34] !== t13) {
    t14 = <ModeCard isActive={t10} onClick={t11} testId="bizz-booking-mode-inclusive" title={t12} description={t13} />;
    $[31] = t10;
    $[32] = t11;
    $[33] = t12;
    $[34] = t13;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  let t15;
  if ($[36] !== t14 || $[37] !== t9) {
    t15 = <div className="grid gap-3">{t9}{t14}</div>;
    $[36] = t14;
    $[37] = t9;
    $[38] = t15;
  } else {
    t15 = $[38];
  }
  let t16;
  if ($[39] !== allowlistCount || $[40] !== confirmModeSwitch || $[41] !== i18n || $[42] !== pendingMode) {
    t16 = pendingMode ? <Modal onClose={() => setPendingMode(null)} className="max-w-[95vw] sm:max-w-md"><Modal.Header title={pendingMode === "EXCLUSIVE" ? i18n._("bizz.booking.edit.mode.switchModal.toExclusive.title", undefined, {
        message: "Switch to Exclusive mode?"
      }) : i18n._("bizz.booking.edit.mode.switchModal.toInclusive.title", undefined, {
        message: "Switch to Inclusive mode?"
      })} description={pendingMode === "INCLUSIVE" ? i18n._("bizz.booking.edit.mode.switchModal.toInclusive.description", {
        count: allowlistCount
      }, {
        message: "Your current catalog has {count} activities listed. Switching modes will clear that list, and all rule filters (regions, categories, budget) are switched back on \u2014 in Inclusive mode the catalog is exactly the activities you pick."
      }) : i18n._("bizz.booking.edit.mode.switchModal.description", {
        count: allowlistCount
      }, {
        message: "Your current catalog has {count} activities listed. Switching modes will clear that list. Other rules (regions, categories, budget) stay as they are."
      })} onClose={() => setPendingMode(null)} /><Modal.Footer><Button variant="link" size="md" onClick={() => setPendingMode(null)}>{i18n._("Cancel")}</Button><Button variant="secondary" size="md" onClick={confirmModeSwitch}>{i18n._("bizz.booking.edit.mode.switchModal.confirm", undefined, {
            message: "Switch mode"
          })}</Button></Modal.Footer></Modal> : null;
    $[39] = allowlistCount;
    $[40] = confirmModeSwitch;
    $[41] = i18n;
    $[42] = pendingMode;
    $[43] = t16;
  } else {
    t16 = $[43];
  }
  let t17;
  if ($[44] !== t15 || $[45] !== t16 || $[46] !== t3) {
    t17 = <section className="flex flex-col gap-3" data-testid="bizz-booking-mode-switch">{t3}{t15}{t16}</section>;
    $[44] = t15;
    $[45] = t16;
    $[46] = t3;
    $[47] = t17;
  } else {
    t17 = $[47];
  }
  return t17;
};
const ModeCard: React.FC<{
  isActive: boolean;
  onClick: () => void;
  title: string;
  description: string;
  testId?: string;
  children?: React.ReactNode;
}> = props => {
  const $ = _c(31);
  const t0 = props.onClick;
  const t1 = props.isActive;
  const t2 = props.testId;
  const t3 = props.isActive ? "border-secondary bg-secondary-50/40" : "border-blue-200 hover:border-secondary/50";
  let t4;
  if ($[0] !== t3) {
    t4 = cx("flex flex-col gap-2 rounded-2xl border p-4 text-left transition", t3);
    $[0] = t3;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const t5 = props.isActive ? "border-secondary" : "border-blue-300";
  let t6;
  if ($[2] !== t5) {
    t6 = cx("mt-0.5 grid size-4 shrink-0 place-items-center rounded-full border", t5);
    $[2] = t5;
    $[3] = t6;
  } else {
    t6 = $[3];
  }
  let t7;
  if ($[4] !== props.isActive) {
    t7 = props.isActive ? <span className="size-2 rounded-full bg-secondary" /> : null;
    $[4] = props.isActive;
    $[5] = t7;
  } else {
    t7 = $[5];
  }
  let t8;
  if ($[6] !== t6 || $[7] !== t7) {
    t8 = <span className={t6}>{t7}</span>;
    $[6] = t6;
    $[7] = t7;
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  let t9;
  if ($[9] !== props.isActive) {
    t9 = props.isActive ? <Icon name="check" size="xs" className="text-secondary" /> : null;
    $[9] = props.isActive;
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  let t10;
  if ($[11] !== props.title || $[12] !== t9) {
    t10 = <span className="flex items-center gap-2 text-sm font-semibold text-blue">{props.title}{t9}</span>;
    $[11] = props.title;
    $[12] = t9;
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  let t11;
  if ($[14] !== props.description) {
    t11 = <span className="text-xs text-blue-500">{props.description}</span>;
    $[14] = props.description;
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  let t12;
  if ($[16] !== t10 || $[17] !== t11) {
    t12 = <span className="flex flex-1 flex-col gap-1">{t10}{t11}</span>;
    $[16] = t10;
    $[17] = t11;
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  let t13;
  if ($[19] !== t12 || $[20] !== t8) {
    t13 = <span className="flex items-start gap-2">{t8}{t12}</span>;
    $[19] = t12;
    $[20] = t8;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  let t14;
  if ($[22] !== props.children) {
    t14 = props.children ? <div className="ml-6 mt-1" onClick={_temp}>{props.children}</div> : null;
    $[22] = props.children;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  let t15;
  if ($[24] !== props.isActive || $[25] !== props.onClick || $[26] !== props.testId || $[27] !== t13 || $[28] !== t14 || $[29] !== t4) {
    t15 = <button type="button" onClick={t0} aria-pressed={t1} data-testid={t2} className={t4}>{t13}{t14}</button>;
    $[24] = props.isActive;
    $[25] = props.onClick;
    $[26] = props.testId;
    $[27] = t13;
    $[28] = t14;
    $[29] = t4;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  return t15;
};
function _temp(e) {
  return e.stopPropagation();
}